/**
 * Single Stone accessibility remediation (ported from the Symmetry 5.5.1 live theme).
 *
 * Markup that the theme owns is fixed directly in Liquid. This file only covers:
 *  - DOM injected by apps (Wishlist Hero, the contact widget), and
 *  - keyboard behaviour Symmetry 8.3.2 still lacks (mobile menu, mobile filters,
 *    sort dropdown, first desktop dropdown).
 *
 * All DOM patching runs from a single, frame-throttled MutationObserver.
 */
(() => {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const mobileQuery = window.matchMedia('(max-width: 767.98px)');

  const isVisible = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && getComputedStyle(el).visibility !== 'hidden';
  };
  const focusableIn = (container) => [...container.querySelectorAll(FOCUSABLE)].filter(isVisible);

  /**
   * Keeps Tab / Shift+Tab inside a container.
   * @param {KeyboardEvent} evt - Keydown event.
   * @param {Element} container - Element to trap focus in.
   */
  const trapTab = (evt, container) => {
    const focusable = focusableIn(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!container.contains(document.activeElement)) {
      evt.preventDefault();
      first.focus();
    } else if (evt.shiftKey && document.activeElement === first) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && document.activeElement === last) {
      evt.preventDefault();
      first.focus();
    }
  };

  /* ---------------------------------------------------------------- DOM patches */

  const productNameFor = (button) => {
    const card = button.closest('.product-block');
    if (card) return card.querySelector('.product-block__title')?.textContent.trim();
    return document.querySelector('.product-info h1, h1')?.textContent.trim();
  };

  const patches = [
    // Wishlist Hero: header icon has no accessible name
    () => {
      document.querySelectorAll('a.wishlist-hero-header-icon').forEach((link) => {
        link.setAttribute('aria-label', 'Open wishlist');
      });
    },

    // Wishlist Hero: every button is "ADD TO WISHLIST" — name the product
    () => {
      document.querySelectorAll('button[aria-label="ADD TO WISHLIST"]').forEach((button) => {
        const name = productNameFor(button);
        if (!name) return;
        const label = `Add ${name} to Wishlist`;
        button.setAttribute('aria-label', label);
        button.setAttribute('title', label);
      });
    },

    // Links that open a new tab should say so
    () => {
      document.querySelectorAll('a[target="_blank"]:not([data-new-tab-labelled])').forEach((link) => {
        const href = link.getAttribute('href') || '';
        const text = link.textContent.trim();
        if (href.includes('shopify.com') && !text) return;
        const current = link.getAttribute('aria-label');
        if (current && /new tab|new window|opens/i.test(current)) return;
        const base = current || text;
        if (!base) return;
        link.setAttribute('aria-label', `${base} (opens in a new tab)`);
        link.dataset.newTabLabelled = 'true';
      });
    },

    // Contact widget launcher is a div with tabindex but no role
    () => {
      document.querySelectorAll('div[tabindex="0"]:not([role])').forEach((el) => {
        if (el.textContent.trim() === 'Get in Touch') el.setAttribute('role', 'button');
      });
    },

    // Contact widget dialog
    () => {
      const dialog = document.querySelector('.wsg-outer[role="dialog"]');
      if (!dialog) return;
      dialog.setAttribute('aria-modal', 'true');
      if (!dialog.getAttribute('aria-label')) dialog.setAttribute('aria-label', 'Contact Us');
    },

    // Announcement-bar localization form is cloned into the mobile drawer: de-duplicate IDs
    () => {
      const footer = document.querySelector('.mobile-navigation-drawer__footer');
      if (!footer) return;
      footer.querySelectorAll('[id]:not([data-mobile-id])').forEach((el) => {
        const oldId = el.id;
        const newId = `${oldId}-mobile`;
        el.id = newId;
        el.dataset.mobileId = 'true';
        footer.querySelectorAll(`[for="${oldId}"]`).forEach((ref) => ref.setAttribute('for', newId));
        ['aria-controls', 'aria-labelledby', 'aria-describedby', 'form'].forEach((attr) => {
          footer.querySelectorAll(`[${attr}~="${oldId}"]`).forEach((ref) => {
            ref.setAttribute(attr, ref.getAttribute(attr).split(' ').map((v) => (v === oldId ? newId : v)).join(' '));
          });
        });
      });
    },

    // Mobile menu: submenu toggles are real buttons with state; closed panels are inert
    () => {
      document.querySelectorAll('.mobile-navigation-drawer .navigation__item--with-children').forEach((item, index) => {
        const link = item.querySelector(':scope > .navigation__link');
        const toggle = item.querySelector(':scope > .navigation__children-toggle');
        const panel = item.querySelector(':scope > .navigation__child-tier');
        if (!link || !toggle || !panel) return;

        const name = link.textContent.trim();
        const isOpen = item.classList.contains('navigation__item--open');
        if (!panel.id) panel.id = `mobile-submenu-${index}`;

        toggle.setAttribute('role', 'button');
        toggle.setAttribute('aria-label', `${isOpen ? 'Collapse' : 'Expand'} ${name} menu`);
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        toggle.setAttribute('aria-controls', panel.id);
        if (link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (link.hasAttribute('aria-controls')) link.setAttribute('aria-controls', panel.id);

        panel.inert = !isOpen;
      });
    },

    // Mobile grid layout switchers expose their pressed state
    () => {
      document.querySelectorAll('.layout-switch').forEach((el) => {
        el.setAttribute('aria-pressed', el.classList.contains('layout-switch--active') ? 'true' : 'false');
      });
    },

    // Closed mobile filter drawer must not be reachable with Tab
    () => {
      document.querySelectorAll('.filter-container').forEach((container) => {
        const filters = container.querySelector('.filters');
        if (!filters) return;
        const open = container.classList.contains('filter-container--show-filters-mobile');
        filters.inert = mobileQuery.matches && !open;
      });
    }
  ];

  let scheduled = false;
  const runPatches = () => {
    scheduled = false;
    patches.forEach((patch) => {
      try {
        patch();
      } catch (err) {
        console.warn('accessibility-fixes:', err);
      }
    });
  };
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(runPatches);
  };

  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'aria-label']
  });
  mobileQuery.addEventListener('change', schedule);
  runPatches();

  /* ---------------------------------------------------------------- keyboard behaviour */

  // First desktop dropdown ("Shop"): Enter/Space toggles it, Escape closes it
  const desktopShop = document.querySelector('.navigation:not(.mobile-navigation-drawer .navigation) .navigation__tier-1 > .navigation__item--with-children > .navigation__link[aria-controls="NavigationTier2-1"]');
  if (desktopShop) {
    const item = desktopShop.parentElement;
    const setOpen = (open) => {
      item.classList.toggle('navigation__item--show-children', open);
      desktopShop.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    desktopShop.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Enter' && evt.key !== ' ') return;
      evt.preventDefault();
      setOpen(desktopShop.getAttribute('aria-expanded') !== 'true');
    });
    document.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Escape' || desktopShop.getAttribute('aria-expanded') !== 'true') return;
      evt.preventDefault();
      setOpen(false);
      desktopShop.focus();
    });
  }

  // Filter group headings (toggle-target, role=button) only respond to Enter natively
  document.addEventListener('keydown', (evt) => {
    if (evt.key !== ' ') return;
    const heading = evt.target.closest?.('toggle-target.filter-group__heading');
    if (!heading) return;
    evt.preventDefault();
    heading.click();
  });

  let contactTrigger = null;
  document.addEventListener('click', (evt) => {
    const trigger = evt.target.closest('a, button, [role="button"]');
    if (trigger && !trigger.closest('.wsg-outer')) contactTrigger = trigger;
  }, true);

  document.addEventListener('keydown', (evt) => {
    // Contact widget dialog: trap focus, Escape closes and returns focus
    const contactDialog = document.querySelector('.wsg-outer[role="dialog"]');
    if (contactDialog && isVisible(contactDialog)) {
      if (evt.key === 'Tab') trapTab(evt, contactDialog);
      if (evt.key === 'Escape') {
        const close = contactDialog.querySelector('.wsg-close, .wsg-close-btn, [aria-label="Close"], button');
        if (close) {
          evt.preventDefault();
          close.click();
          if (contactTrigger) setTimeout(() => contactTrigger.focus(), 50);
        }
      }
      return;
    }

    // Mobile menu: Escape goes back a level, then closes; Tab stays inside
    const drawer = document.querySelector('.mobile-navigation-drawer');
    if (drawer && document.body.classList.contains('reveal-mobile-nav')) {
      if (evt.key === 'Tab') trapTab(evt, drawer);
      if (evt.key === 'Escape') {
        evt.preventDefault();
        const back = drawer.classList.contains('mobile-navigation-drawer--child-open')
          && [...drawer.querySelectorAll('.mobile-nav-back')].find(isVisible);
        const close = [...drawer.querySelectorAll('.mobile-nav-toggle')].find(isVisible);
        (back || close)?.click();
      }
      return;
    }

    // Mobile filters: Escape closes and returns focus to the Filter button; Tab stays inside
    const openFilters = mobileQuery.matches && document.querySelector('.filter-container--show-filters-mobile .filters');
    if (openFilters) {
      if (evt.key === 'Tab') trapTab(evt, openFilters);
      if (evt.key === 'Escape') {
        evt.preventDefault();
        openFilters.querySelector('.filters__close')?.click();
        setTimeout(() => {
          [...document.querySelectorAll('[data-toggle-filters]')]
            .find((el) => !el.classList.contains('filters__close') && !el.classList.contains('filter-shade') && isVisible(el))
            ?.focus();
        }, 100);
      }
      return;
    }

    // Sort dropdown: Escape closes and returns focus to its button
    if (evt.key === 'Escape') {
      const sortButton = document.querySelector('.link-dropdown__button[aria-controls="sort-dropdown-options"][aria-expanded="true"]');
      if (sortButton) {
        evt.preventDefault();
        sortButton.click();
        setTimeout(() => sortButton.focus(), 50);
      }
    }
  });
})();
